import { useState, useEffect, useMemo } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import { api } from '../api';

const LEAVE_COLOURS: Record<string, string> = {
  annual: 'bg-blue-100 text-blue-800 border-blue-200',
  sick: 'bg-orange-100 text-orange-800 border-orange-200',
  maternity: 'bg-pink-100 text-pink-800 border-pink-200',
  paternity: 'bg-purple-100 text-purple-800 border-purple-200',
  bank_holiday: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  rest_day: 'bg-gray-100 text-gray-600 border-gray-200',
  training: 'bg-teal-100 text-teal-800 border-teal-200',
  suspension: 'bg-red-100 text-red-800 border-red-200',
  other: 'bg-gray-100 text-gray-600 border-gray-200',
};

const STATUS_COLOURS: Record<string, string> = {
  worked: 'bg-green-100 border-green-300',
  missing: 'bg-red-50 border-red-300',
  future: 'bg-gray-50 border-gray-200',
  ...Object.fromEntries(
    Object.entries(LEAVE_COLOURS).map(([k, v]) => [k, v])
  ),
};

function fmtMins(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + 'm' : ''}` : `${m}m`;
}

export function CalendarPage() {
  const [drivers, setDrivers] = useState<any[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<number | null>(null);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [calendar, setCalendar] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveDate, setLeaveDate] = useState('');
  const [leaveType, setLeaveType] = useState('rest_day');
  const [leaveNotes, setLeaveNotes] = useState('');
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkFrom, setBulkFrom] = useState('');
  const [bulkTo, setBulkTo] = useState('');

  useEffect(() => {
    api.getDrivers({ page_size: 500 }).then(d => setDrivers(Array.isArray(d) ? d : d.drivers || [])).catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedDriver) return;
    setLoading(true);
    api.getCalendar(selectedDriver, month)
      .then(setCalendar)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedDriver, month]);

  const prevMonth = () => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const nextMonth = () => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthLabel = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
  }, [month]);

  async function handleAddLeave() {
    if (!selectedDriver) return;
    try {
      if (bulkMode && bulkFrom && bulkTo) {
        await api.addLeaveBulk({ driver_id: selectedDriver, date_from: bulkFrom, date_to: bulkTo, leave_type: leaveType, notes: leaveNotes || undefined });
      } else if (leaveDate) {
        await api.addLeave({ driver_id: selectedDriver, date: leaveDate, leave_type: leaveType, notes: leaveNotes || undefined });
      }
      setShowLeaveModal(false);
      setLeaveNotes('');
      // Refresh
      const data = await api.getCalendar(selectedDriver, month);
      setCalendar(data);
    } catch (err: any) {
      alert(err.message);
    }
  }

  async function handleDeleteLeave(leaveId: number) {
    if (!selectedDriver) return;
    await api.deleteLeave(leaveId);
    const data = await api.getCalendar(selectedDriver, month);
    setCalendar(data);
  }

  return (
    <PortalLayout currentPage="calendar">
      <div className="p-5">
        <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-900 mb-6">Worker Calendar</h1>

        {/* Driver selector */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <select
            className="border border-gray-300 rounded px-4 py-2 text-sm bg-white"
            value={selectedDriver || ''}
            onChange={(e) => setSelectedDriver(Number(e.target.value) || null)}
          >
            <option value="">Select a driver...</option>
            {drivers.map((d: any) => (
              <option key={d.id} value={d.id}>{d.first_names} {d.surname}</option>
            ))}
          </select>

          {selectedDriver && (
            <>
              <div className="flex items-center gap-2">
                <button onClick={prevMonth} className="px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 text-sm">&larr;</button>
                <span className="text-sm font-semibold min-w-[140px] text-center">{monthLabel}</span>
                <button onClick={nextMonth} className="px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 text-sm">&rarr;</button>
              </div>

              <button
                onClick={() => { setShowLeaveModal(true); setBulkMode(false); }}
                className="ml-auto px-4 py-2 bg-slate-700 text-white rounded text-sm hover:bg-slate-800"
              >
                + Add Leave
              </button>
            </>
          )}
        </div>

        {/* Summary bar */}
        {calendar?.summary && (
          <div className="grid grid-cols-5 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{calendar.summary.worked_days}</p>
              <p className="text-xs text-green-600">Worked Days</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-4 text-center">
              <p className="text-2xl font-bold text-gray-600">{calendar.summary.rest_days || 0}</p>
              <p className="text-xs text-gray-500">Rest Days</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{calendar.summary.leave_days}</p>
              <p className="text-xs text-blue-600">Leave Days</p>
            </div>
            <div className={`border rounded p-4 text-center ${calendar.summary.missing_days > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
              <p className={`text-2xl font-bold ${calendar.summary.missing_days > 0 ? 'text-red-700' : 'text-gray-500'}`}>{calendar.summary.missing_days}</p>
              <p className={`text-xs ${calendar.summary.missing_days > 0 ? 'text-red-600' : 'text-gray-400'}`}>Missing Days</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-4 text-center">
              <p className="text-2xl font-bold text-gray-700">{calendar.summary.total_days}</p>
              <p className="text-xs text-gray-500">Total Days</p>
            </div>
          </div>
        )}

        {/* Calendar grid */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading calendar...</div>
        ) : calendar?.days ? (
          <div className="bg-white border border-gray-200 rounded overflow-hidden">
            {/* Day headers */}
            <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                <div key={d} className="px-2 py-2 text-center text-xs font-semibold text-gray-500">{d}</div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7">
              {/* Offset for first day of month */}
              {calendar.days.length > 0 && (() => {
                const dayIdx = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(calendar.days[0].day_of_week);
                return Array.from({ length: dayIdx >= 0 ? dayIdx : 0 }).map((_, i) => (
                  <div key={`pad-${i}`} className="min-h-[80px] border-b border-r border-gray-100" />
                ));
              })()}

              {calendar.days.map((day: any) => {
                const statusClass = STATUS_COLOURS[day.status] || 'bg-gray-50 border-gray-200';
                const dayNum = new Date(day.date).getDate();
                return (
                  <div
                    key={day.date}
                    className={`min-h-[80px] border-b border-r border-gray-100 p-1.5 ${statusClass} cursor-pointer hover:opacity-80 transition-opacity`}
                    onClick={() => {
                      if (day.status === 'missing') {
                        setLeaveDate(day.date);
                        setShowLeaveModal(true);
                        setBulkMode(false);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold ${day.is_weekend ? 'text-gray-400' : 'text-gray-700'}`}>{dayNum}</span>
                      {day.status === 'missing' && (
                        <span className="text-[10px] font-bold text-red-500 bg-red-100 px-1 rounded">MISSING</span>
                      )}
                    </div>
                    {day.status === 'worked' && day.driving_minutes != null && (
                      <div className="mt-1 space-y-0.5">
                        <p className="text-[10px] text-green-700">Drive: {fmtMins(day.driving_minutes)}</p>
                        {day.work_minutes > 0 && <p className="text-[10px] text-blue-600">Work: {fmtMins(day.work_minutes)}</p>}
                        {day.distance_km > 0 && <p className="text-[10px] text-gray-500">{day.distance_km} km</p>}
                      </div>
                    )}
                    {day.leave_type && (
                      <div className="mt-1">
                        <span className="text-[10px] font-medium">{day.leave_type.replace('_', ' ')}</span>
                        {day.leave_id && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteLeave(day.leave_id); }}
                            className="ml-1 text-[10px] text-red-400 hover:text-red-600"
                          >×</button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : selectedDriver ? (
          <div className="text-center py-12 text-gray-400">No calendar data available</div>
        ) : (
          <div className="text-center py-12 text-gray-400">Select a driver to view their calendar</div>
        )}

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-3">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-green-200 border border-green-300" /><span className="text-xs text-gray-500">Worked</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-100 border border-red-300" /><span className="text-xs text-gray-500">Missing</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-blue-100 border border-blue-200" /><span className="text-xs text-gray-500">Annual Leave</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-orange-100 border border-orange-200" /><span className="text-xs text-gray-500">Sick</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-gray-100 border border-gray-200" /><span className="text-xs text-gray-500">Rest Day</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-teal-100 border border-teal-200" /><span className="text-xs text-gray-500">Training</span></div>
        </div>

        {/* Leave modal */}
        {showLeaveModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded shadow-xl p-6 w-full max-w-md">
              <h3 className="text-sm font-semibold mb-4">
                {bulkMode ? 'Add Leave (Date Range)' : 'Add Leave Entry'}
              </h3>

              <div className="mb-3 flex gap-2">
                <button
                  onClick={() => setBulkMode(false)}
                  className={`px-3 py-1.5 text-sm rounded ${!bulkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-600'}`}
                >Single Day</button>
                <button
                  onClick={() => setBulkMode(true)}
                  className={`px-3 py-1.5 text-sm rounded ${bulkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-600'}`}
                >Date Range</button>
              </div>

              {bulkMode ? (
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">From</label>
                    <input type="date" value={bulkFrom} onChange={(e) => setBulkFrom(e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">To</label>
                    <input type="date" value={bulkTo} onChange={(e) => setBulkTo(e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                </div>
              ) : (
                <div className="mb-3">
                  <label className="block text-sm text-gray-600 mb-1">Date</label>
                  <input type="date" value={leaveDate} onChange={(e) => setLeaveDate(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
              )}

              <div className="mb-3">
                <label className="block text-sm text-gray-600 mb-1">Leave Type</label>
                <select value={leaveType} onChange={(e) => setLeaveType(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                  <option value="rest_day">Rest Day</option>
                  <option value="annual">Annual Leave</option>
                  <option value="sick">Sick</option>
                  <option value="bank_holiday">Bank Holiday</option>
                  <option value="maternity">Maternity</option>
                  <option value="paternity">Paternity</option>
                  <option value="training">Training</option>
                  <option value="suspension">Suspension</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-1">Notes (optional)</label>
                <input type="text" value={leaveNotes} onChange={(e) => setLeaveNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="e.g. Doctor's appointment" />
              </div>

              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowLeaveModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                <button onClick={handleAddLeave} className="px-4 py-2 text-sm bg-slate-700 text-white rounded hover:bg-slate-800">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
